import { Modal } from "@monsterindia/global-ui-components";
import React, { useCallback, useEffect, useState } from "react";

import Skeleton from "@/components/dashboard/applied/jobStatusDescription/skeleton";

import { useAppliedPageData } from "..";
import { useDashboardContext } from "../..";
import { useJobDetails } from "../../recommended/jobDescription/query";
import SkeletonJobCard from "../appliedJobCard/skeleton";
import { AppliedPageContextProps, DashboardContextProps } from "../types";
import {
  PrepAIQuestionsResponse,
  prepAIQuestionsAPI,
  useJobStatusAndActivity,
  useSimilarJobs,
} from "./query";
import JobStatusCard from "./view";

const JobStatusDescription = () => {
  const { isMobile, host }: DashboardContextProps = useDashboardContext();
  const { appliedJobData, setAppliedJobData }: AppliedPageContextProps =
    useAppliedPageData();

  const [selectedData, setSelectedData] = useState(
    appliedJobData.urlQueries?.jobId &&
      appliedJobData.urlQueries?.applicationId &&
      appliedJobData.jobDescIndex?.selectedIndex === null
      ? {}
      : appliedJobData?.data?.[
          !appliedJobData.jobDescIndex?.selectedIndex
            ? 0
            : appliedJobData.jobDescIndex?.selectedIndex
        ] || {},
  );

  const [prepAIQuestionData, setPrepAIQuestionData] = useState<
    PrepAIQuestionsResponse | Error | undefined
  >(undefined);
  const [loader, setLoader] = useState(true);
  const [enableAPI, setEnableAPI] = useState({
    enableJobStatusFetch:
      Object.keys(selectedData).length > 0 &&
      !appliedJobData.similarJobsSelected &&
      !appliedJobData.newDataLoader,
    enableSimilarJobsFetch:
      Object.keys(selectedData).length > 0 && !appliedJobData.newDataLoader,
    enableJobDetailsFetch:
      appliedJobData.urlQueries?.jobId &&
      appliedJobData.urlQueries?.applicationId &&
      appliedJobData.jobDescIndex?.selectedIndex === null,
  });
  const { data: similarJobs } = useSimilarJobs(
    selectedData.jobDetails?.id,
    true,
    appliedJobData?.profilePersonalDetails?.personalDetailSection
      ?.personalDetails?.additionalPersonalDetail?.experience?.years || 0,
    enableAPI.enableSimilarJobsFetch,
  );

  const { data: jobStatus } = useJobStatusAndActivity(
    selectedData?.applicationData?.applicationId,
    enableAPI.enableJobStatusFetch,
  );

  const { data: jobDetails } = useJobDetails(
    "",
    appliedJobData.urlQueries?.jobId || "",
    appliedJobData?.profilePersonalDetails?.personalDetailSection
      ?.personalDetails?.uuid || "",
    appliedJobData?.profilePersonalDetails?.isAutoRegistered || false,
    enableAPI?.enableJobDetailsFetch || false,
  );

  const mediaUrl = appliedJobData.siteProps?.media_url || "media.foundit.in";

  const setLoaderOff = useCallback(() => {
    setTimeout(() => {
      setLoader(false);
      if (!appliedJobData.showMobileModal) {
        setTimeout(() => {
          document.body.style.overflow = "auto";
        }, 100);
      }
    }, 200);
  }, [loader]);

  const prepAIQuestionHandler = useCallback(
    async (id?: string | undefined) => {
      const response: PrepAIQuestionsResponse | Error | undefined =
        await prepAIQuestionsAPI(id || selectedData.jobDetails?.id);
      setPrepAIQuestionData(response);
    },
    [isMobile, selectedData],
  );

  useEffect(() => {
    const queryData =
      appliedJobData.urlQueries?.jobId &&
      appliedJobData.urlQueries?.applicationId &&
      appliedJobData.jobDescIndex?.selectedIndex === null;
    if (!queryData && isMobile && !appliedJobData?.similarJobsSelected) {
      prepAIQuestionHandler();
    }
  }, []);

  useEffect(() => {
    if (appliedJobData.filterDisable) {
      setAppliedJobData({
        ...appliedJobData,
        jobDescIndex: { selectedIndex: isMobile ? undefined : 0 },
        filterDisable: false,
      });
    }
  }, [appliedJobData.data]);

  useEffect(() => {
    const queryData =
      appliedJobData.urlQueries?.jobId &&
      appliedJobData.urlQueries?.applicationId &&
      appliedJobData.jobDescIndex?.selectedIndex === null;
    if (!isMobile && !queryData) {
      const newSelectedData =
        appliedJobData.data?.[
          appliedJobData.jobDescIndex?.selectedIndex || 0
        ] || {};
      setSelectedData(newSelectedData);
      setEnableAPI({
        enableJobStatusFetch:
          Object.keys(newSelectedData).length > 0 &&
          !appliedJobData.similarJobsSelected &&
          !appliedJobData.newDataLoader,
        enableSimilarJobsFetch:
          Object.keys(newSelectedData).length > 0 &&
          !appliedJobData.newDataLoader,
        enableJobDetailsFetch: queryData,
      });
      if (
        selectedData?.jobDetails?.id ==
        appliedJobData.data?.[appliedJobData.jobDescIndex?.selectedIndex || 0]
          ?.jobDetails?.id
      ) {
        setLoaderOff();
      }
    }
  }, [appliedJobData.jobDescIndex]);

  useEffect(() => {
    if (jobDetails) {
      const newSelectedData = {
        jobDetails: jobDetails,
        applicationData: {
          applicationId: appliedJobData.urlQueries?.applicationId,
        },
      };
      setSelectedData({
        jobDetails: jobDetails,
        applicationData: {
          applicationId: appliedJobData.urlQueries?.applicationId,
        },
      });
      setEnableAPI({
        enableJobStatusFetch:
          Object.keys(newSelectedData).length > 0 &&
          !appliedJobData.similarJobsSelected,
        enableSimilarJobsFetch: Object.keys(newSelectedData).length > 0,
        enableJobDetailsFetch:
          appliedJobData.urlQueries?.jobId &&
          appliedJobData.urlQueries?.applicationId &&
          appliedJobData.jobDescIndex?.selectedIndex === null,
      });
      if (isMobile && !appliedJobData?.similarJobsSelected) {
        prepAIQuestionHandler(jobDetails.id);
      }
    }
  }, [jobDetails]);

  useEffect(() => {
    if (appliedJobData.isLoadingJobDesc) {
      setLoader(true);
      document.body.style.overflow = "hidden";
    }
  }, [appliedJobData.isLoadingJobDesc]);

  useEffect(() => {
    if (isMobile) {
      if (similarJobs && jobStatus && prepAIQuestionData) {
        setLoaderOff();
      }
    } else if (similarJobs && jobStatus) {
      setLoaderOff();
    }
  }, [similarJobs, jobStatus, prepAIQuestionData]);

  useEffect(() => {
    if (appliedJobData.similarJobsSelected && similarJobs) {
      setLoaderOff();
    }
  }, [appliedJobData.similarJobsSelected, similarJobs]);

  useEffect(() => {
    if (isMobile && appliedJobData.createViewportResizeHandler) {
      const initialHeight = window.innerHeight;
      const handleExtraViewPortJobDesc =
        appliedJobData.createViewportResizeHandler(
          initialHeight,
          "desc",
          setAppliedJobData,
        );

      window.addEventListener("resize", handleExtraViewPortJobDesc);

      return () => {
        window.removeEventListener("resize", handleExtraViewPortJobDesc);
      };
    } else {
      return () => {};
    }
  }, []);

  const jobStatusMobileCardComponent = (
    <div>
      <div
        className={`${loader || appliedJobData.isLoadingJobDesc ? "hidden" : ""}`}
      >
        <JobStatusCard
          host={host}
          selectedData={selectedData}
          jobDescData={{
            similarJobs,
            jobStatus,
            similarJobsSelected: appliedJobData.similarJobsSelected,
            prepAIData: prepAIQuestionData,
            filterStatusPresent: appliedJobData.filterStatusPresent,
          }}
          siteProps={appliedJobData?.siteProps || {}}
          sitePropsProperties={appliedJobData?.sitePropsProperties || {}}
          loader={loader}
          mediaUrl={mediaUrl}
          leftSideRef={appliedJobData.leftSideRef}
        />
      </div>
      {!appliedJobData.similarJobsSelected ? (
        <div
          className={`${!loader && !appliedJobData.isLoadingJobDesc ? "hidden" : ""}`}
        >
          <Skeleton isMobile={isMobile} />
        </div>
      ) : (
        <div
          className={`${!loader && !appliedJobData.isLoadingJobDesc ? "hidden" : ""} flex !h-[100vh] flex-col gap-4`}
        >
          {Array(6)
            .fill(0)
            .map(() => {
              return <SkeletonJobCard />;
            })}
        </div>
      )}
    </div>
  );

  const jobStatusCardComponent = (
    <div>
      {loader || appliedJobData.isLoadingJobDesc ? (
        <Skeleton isMobile={isMobile} />
      ) : (
        <div
          className={`${loader || appliedJobData.isLoadingJobDesc ? "hidden" : ""}`}
        >
          <JobStatusCard
            host={host}
            selectedData={selectedData}
            jobDescData={{
              similarJobs,
              jobStatus,
              similarJobsSelected: false,
              prepAIData: prepAIQuestionData,
              filterStatusPresent: appliedJobData.filterStatusPresent,
            }}
            siteProps={appliedJobData?.siteProps || {}}
            sitePropsProperties={appliedJobData?.sitePropsProperties || {}}
            loader={loader}
            mediaUrl={mediaUrl}
            leftSideRef={appliedJobData.leftSideRef}
          />
        </div>
      )}
    </div>
  );

  return isMobile ? (
    <Modal
      showModal={appliedJobData.showMobileModal}
      closeModal={() => {
        document.body.style.overflow = "auto";
        setAppliedJobData({
          ...appliedJobData,
          jobDescIndex: { selectedIndex: isMobile ? undefined : 0 },
          showMobileModal: false,
          similarJobsSelected: false,
        });
      }}
      screenAlignment="bottom-sheet"
      style={{ maxHeight: "100%", borderRadius: "0px", padding: "0px" }}
      customContentContainerClass="!max-h-[100vh] !rounded-none !p-0"
      header={
        appliedJobData.similarJobsSelected ? (
          <div>
            <p className="ml-[4px] w-[100%] text-[14px] font-bold leading-[20px] text-[#17142A]">
              Similar Jobs
            </p>
          </div>
        ) : (
          true
        )
      }
      headerType="backButton"
      showCloseButton={false}
      transition={false}
    >
      {appliedJobData?.extraViewPortData?.enabled ? (
        <div
          className={`${!appliedJobData.similarJobsSelected ? "bg-jdPage-gradient" : ""} !-ml-4 !-mr-4 !-mt-4 h-[49px]`}
        ></div>
      ) : null}
      {jobStatusMobileCardComponent}
    </Modal>
  ) : (
    jobStatusCardComponent
  );
};

export default React.memo(JobStatusDescription);
